/**
 * SketchItineraryTable Component
 * 
 * Displays sketch itinerary as a simple 3-column table.
 * Uses normalized data from QuotationFormatter.getSketchItinerary()
 * 
 * Usage:
 *   const formatter = new QuotationFormatter(quotation, agency);
 *   const sketchData = formatter.getSketchItinerary();
 *   
 *   <SketchItineraryTable
 *     data={sketchData}
 *     packageType={quotation.route_type}
 *     variant="detail"
 *   />
 */

import React from 'react';
import { Calendar } from 'lucide-react';
import { COMPONENT_VARIANTS, formatItineraryTitle } from './displayConfig';

const SketchItineraryTable = ({ 
  data = [],
  packageType = 'custom',
  variant = 'detail',
  showTitle = true,
  className = ''
}) => {
  // Filter out empty itinerary items (where title/highlights are both empty)
  // Bug C6 fix (2026-04-30): tolerate snake_case fields (`overnight_at`) returned
  // by /quotations/calculate-custom-package alongside the camelCase `overnightAt`
  // produced by QuotationFormatter — neither side has to remap.
  const validData = (data || [])
    .map((item) => ({
      ...item,
      overnightAt: item.overnightAt || item.overnight_at || item.overnightStay || '',
      day: item.day || item.title || '',
    }))
    .filter((item) => item.title || item.highlights || item.description || item.overnightAt || item.day);

  if (!validData || validData.length === 0) {
    return null;
  }

  const styles = COMPONENT_VARIANTS[variant] || COMPONENT_VARIANTS.detail;

  return (
    <div 
      className={className}
      data-package-type={packageType}
      data-component="sketch-itinerary"
    >
      {showTitle && (
        <h3 className={`font-semibold ${variant === 'preview' ? 'text-sm' : 'text-lg'} mb-3 flex items-center gap-2`}>
          <Calendar className={`${variant === 'preview' ? 'w-4 h-4' : 'w-5 h-5'} text-[var(--navy)]`} />
          Sketch Itinerary
        </h3>
      )}
      
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className={`w-full ${styles.fontSize}`}>
          <thead>
            <tr className={`${styles.headerBg} ${styles.headerText}`}>
              <th className={`${styles.padding} text-left font-semibold w-20`}>Day</th>
              <th className={`${styles.padding} text-left font-semibold`}>Highlights</th>
              <th className={`${styles.padding} text-center font-semibold w-32`}>Overnight Stay</th>
            </tr>
          </thead>
          <tbody>
            {validData.map((item, idx) => (
              <tr 
                key={idx}
                className={idx % 2 === 0 ? 'bg-white' : (variant === 'preview' ? 'bg-slate-50' : 'bg-gray-50')}
              >
                <td className={`${styles.padding} border-t font-semibold text-[var(--navy)]`}>
                  {item.day}
                </td>
                <td className={`${styles.padding} border-t`}>
                  {formatItineraryTitle(item.highlights || item.title || item.description)}
                </td>
                <td className={`${styles.padding} border-t text-center`}>
                  {item.overnightAt ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-[color-mix(in_srgb,var(--cyan)_15%,white)] text-[var(--navy)] text-xs font-medium">
                      {item.overnightAt}
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SketchItineraryTable;
